import { Button } from '@kerfjs/ui/button';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { TabBar } from '@kerfjs/ui/tab-bar';
import { TabBarItem } from '@kerfjs/ui/tab-bar-item';
import { wireScrollDividers } from '@kerfjs/ui/wire-scroll-dividers';
import { wireTabBars } from '@kerfjs/ui/wire-tab-bars';
import type { SafeHtml } from 'kerfjs';
import { Plus } from 'lucide';

import { libraryProjects, libraryScope } from '../../../data/state.js';

/**
 * The project settings pages' secondary toolbar (HS2-91S2YF): a Kerf `TabBar` of the
 * organization's projects, the selected one driving Project › Info and Project ›
 * Knowledge Base, with an adjacent + that creates a project. Selection is manual
 * (Enter/Space/click) because choosing a project loads its library.
 */
export function ProjectTabsHeader(): SafeHtml {
  const selected = libraryScope.value;
  return (
    <TabBar
      id="settings-project-tabs"
      label="Projects"
      activation="manual"
      trailingPlacement="adjacent"
      trailing={
        <Button
          size="compact"
          type="button"
          tone="neutral"
          emphasis="plain"
          action="lib-new-project"
          icon={<LucideIcon icon={Plus} name="" />}
          ariaLabel="New Project"
          title="New project"
        />
      }
    >
      {libraryProjects.value.map((project) => (
        <TabBarItem
          id={project.id}
          name={project.name}
          selected={project.id === selected}
          closable={false}
          selectAction="settings-project"
        />
      ))}
    </TabBar>
  );
}

/** Wire the project tabs once on the page-lifetime root: keyboard roving with manual
 *  activation (the app owns selection; the strip is not reorderable) and the strip's
 *  scrolled-away dividers. Returns the disposer. */
export function wireProjectTabs(root: HTMLElement): () => void {
  const disposeTabs = wireTabBars(root, { activation: 'manual', onReorder: () => undefined });
  const disposeDividers = wireScrollDividers(root);
  return () => {
    disposeTabs();
    disposeDividers();
  };
}
